import { get } from 'lodash';

// SECURITY (AER-1639): moved to REACT_APP_* env vars (set at CRA build time).
// CAVEAT: CRA inlines REACT_APP_* values into the public JS bundle, so these are
// NOT secret — they ship to the browser and are readable by anyone. Env vars only
// remove them from source control; the real fix is server-side proxying of the
// Particle/internal calls. See AUTOTESTING/AER-1639/README.md.
const access_token = process.env.REACT_APP_ACCESS_TOKEN || '';
const particle_key = process.env.REACT_APP_PARTICLE_KEY || '';

const modelPhotos = (env = 'dev'): string => {
  return `https://models-photos-${env}.s3.amazonaws.com/`;
};

export const getEnv = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;
  if (env === 'development') {
    return 'local';
  } else if (env === 'production') {
    return 'prod';
  } else if (env === 'dev') {
    return 'dev';
  } else {
    return 'qa';
  }
};

export const isProd = () => {
  return getEnv() === 'prod';
}

export const getUrl = () => {
  const env = getEnv();
  const base = {
    local: 'http://localhost:3001',
    dev: 'https://dev.aeras-api.link',
    qa: 'https://qa.aeras-api.link',
    prod: 'https://prod.aeras-api.link',
  };
  const baseURL = get(base, env, base.qa);
  return `${baseURL}/api`;
};

export interface IAppConfig {
  aws_identity_pool: string;
  aws_user_pool: string;
  aws_region: string;
  aws_webclient_id: string;

  app_url: string;
  access_token: string;
  model_photos: string;

  particle_key: string;

  ToAddresses: string[];
  FromAddresses: string[];

  pusherChannelName: string;
  pusher: {
    appId: string;
    key: string;
    cluster: string;
  };
}

// SECURITY (AER-1639): AWS Cognito identifiers and Pusher client config are read
// strictly from REACT_APP_* env vars with NO in-source fallbacks. These are public
// client identifiers (Cognito pool/client IDs, Pusher app key) inlined into the
// browser bundle by CRA. Set them in .env.local locally, and in the Netlify build
// env per target (the Pusher app differs per environment). An unset var resolves to
// '' (the app won't connect) — see AUTOTESTING/AER-1639/README.md.
const awsBase = {
  aws_identity_pool: process.env.REACT_APP_AWS_IDENTITY_POOL || '',
  aws_user_pool: process.env.REACT_APP_AWS_USER_POOL || '',
  aws_region: process.env.REACT_APP_AWS_REGION || '',
  aws_webclient_id: process.env.REACT_APP_AWS_WEBCLIENT_ID || '',
};

// Pusher client config per environment (each env is a distinct Pusher app). Values
// come from per-env REACT_APP_PUSHER_*_<ENV> vars — STATIC references so CRA inlines
// them at build time (CRA can't resolve a computed process.env[key], unlike the
// backend's env() helper). getEnv() below selects the block at runtime; set the
// suffixed vars in .env.local locally / Netlify per target. Cluster is shared.
const pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER || '';
const pusherByEnv = {
  dev: {
    appId: process.env.REACT_APP_PUSHER_APP_ID_DEV || '',
    key: process.env.REACT_APP_PUSHER_KEY_DEV || '',
    cluster: pusherCluster,
  },
  qa: {
    appId: process.env.REACT_APP_PUSHER_APP_ID_QA || '',
    key: process.env.REACT_APP_PUSHER_KEY_QA || '',
    cluster: pusherCluster,
  },
  prod: {
    appId: process.env.REACT_APP_PUSHER_APP_ID_PROD || '',
    key: process.env.REACT_APP_PUSHER_KEY_PROD || '',
    cluster: pusherCluster,
  },
};

const config = {
  local: {
    ...awsBase,

    app_url: 'https://qa--vibrant-chandrasekhar-ddde91.netlify.app/',
    access_token,
    model_photos: modelPhotos('dev'),

    particle_key: `${particle_key}`,

    ToAddresses: ['smartutility@dentalez.com'],
    FromAddresses: ['noreply@mg.aerasintel.com'],

    pusherChannelName: 'dentalEZ',
    pusher: pusherByEnv.qa,
  },
  dev: {
    ...awsBase,

    app_url: 'https://qa--vibrant-chandrasekhar-ddde91.netlify.app/',
    access_token,
    model_photos: modelPhotos('dev'),

    particle_key: `${particle_key}`,

    ToAddresses: ['smartutility@dentalez.com'],
    FromAddresses: ['noreply@aeras.dentalez.biz'],

    pusherChannelName: 'dentalEZ',
    pusher: pusherByEnv.dev,
  },
  qa: {
    ...awsBase,

    app_url: 'https://qa--vibrant-chandrasekhar-ddde91.netlify.app/',
    access_token,
    model_photos: modelPhotos('dev'),

    particle_key: `${particle_key}`,

    ToAddresses: ['smartutility@dentalez.com'],
    FromAddresses: ['noreply@mg.aerasintel.com'],

    pusherChannelName: 'dentalEZ',
    pusher: pusherByEnv.qa,
  },
  prod: {
    ...awsBase,

    app_url: 'https://aeras.dentalez.com/',
    access_token,
    model_photos: modelPhotos('dev'),

    particle_key: `${particle_key}`,

    ToAddresses: ['smartutility@dentalez.com'],
    FromAddresses: ['noreply@mg.aerasintel.com'],

    pusherChannelName: 'dentalEZ',
    pusher: pusherByEnv.prod,
  },
};

export type ENVS = 'local' | 'dev' | 'qa' | 'prod';

export default (use?: ENVS): IAppConfig => {
  use = use || getEnv();
  return get(config, use, 'qa') as IAppConfig;
};
